import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Divider,
  FormControl,
  Paper,
  TextField,
  Typography,
  InputAdornment
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { DebounceInput } from 'react-debounce-input';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-fetching-library';

import { AlertDialogContainer } from '../../../components/others/AlertDialogContainer';
import { setToken,
  setGroup,
  setOLOrderFilters,
  setZSOrderFilters,
  setPLOrderFilters,
  setWROrderFilters,
  setOLOrderHiddenColumns,
  setZSOrderHiddenColumns,
  setPLOrderHiddenColumns,
  setWROrderHiddenColumns,
  setOLOrderColumnsWidth,
  setZSOrderColumnsWidth,
  setPLOrderColumnsWidth,
  setWROrderColumnsWidth,
  setOLOrderColumnsSequence,
  setZSOrderColumnsSequence,
  setPLOrderColumnsSequence,
  setWROrderColumnsSequence,
  setOLOrderSearchFields,
  setZSOrderSearchFields,
  setPLOrderSearchFields,
  setWROrderSearchFields,
  setEmployeeId,
  setEmployeeConfigId } from '../../../services/redux-actions';
import { loginAction } from '../../../services/request';
import { useFullScreen } from '../../../services/hooks';
import { useNavigate } from 'react-router-dom';

export const LoginForm = () => {
  const navigate = useNavigate();
  const mediaWidth = useFullScreen() ? '300px' : '450px';
  const dispatch = useDispatch();
  const { loading, mutate, error } = useMutation(loginAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');

  const validationSchema = Yup.object({
    email: Yup.string().required('To pole nie może być puste'),
    password: Yup.string()
      .required('To pole nie może być puste')
      .min(4, 'Hasło musi mieć co najmniej 4 znaki')
      .max(24, 'Hasło może mięc najwyżej 24 znaków')
  });

  const initialValues = {
    email: '',
    password: ''
  };

  const setModuleFilter = payload => {
    if (module === 'obozy') {
      return setOLOrderFilters(payload.user.config.ol_order_filters);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderFilters(payload.user.config.zs_order_filters);
    } else if (module === 'polkolonie') {
      return setPLOrderFilters(payload.user.config.pl_order_filters);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderFilters(payload.user.config.wr_order_filters);
    }
  };

  const setModuleHiddenColumns = payload => {
    if (module === 'obozy') {
      return setOLOrderHiddenColumns(payload.user.config.ol_order_hidden_columns);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderHiddenColumns(payload.user.config.zs_order_hidden_columns);
    } else if (module === 'polkolonie') {
      return setPLOrderHiddenColumns(payload.user.config.pl_order_hidden_columns);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderHiddenColumns(payload.user.config.wr_order_hidden_columns);
    }
  };

  const setModuleColumnsWidth = payload => {
    if (module === 'obozy') {
      return setOLOrderColumnsWidth(payload.user.config.ol_order_columns_width);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderColumnsWidth(payload.user.config.zs_order_columns_width);
    } else if (module === 'polkolonie') {
      return setPLOrderColumnsWidth(payload.user.config.pl_order_columns_width);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderColumnsWidth(payload.user.config.wr_order_columns_width);
    }
  };

  const setModuleColumnsSequence = payload => {
    if (module === 'obozy') {
      return setOLOrderColumnsSequence(payload.user.config.ol_order_columns_sequence);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderColumnsSequence(payload.user.config.zs_order_columns_sequence);
    } else if (module === 'polkolonie') {
      return setPLOrderColumnsSequence(payload.user.config.pl_order_columns_sequence);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderColumnsSequence(payload.user.config.wr_order_columns_sequence);
    }
  };

  const setModuleSearchFields = payload => {
    if (module === 'obozy') {
      return setOLOrderSearchFields(payload.user.config.ol_order_search_fields);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderSearchFields(payload.user.config.zs_order_search_fields);
    } else if (module === 'polkolonie') {
      return setPLOrderSearchFields(payload.user.config.pl_order_search_fields);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderSearchFields(payload.user.config.wr_order_search_fields);
    }
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values);
    if (!error) {
      dispatch(setToken(payload.access));
      dispatch(setGroup(payload.user.groups[0]));
      if (payload.user.groups[0] !== 'clients') {
        if (payload.user.config) {
          dispatch(setEmployeeId(payload.user.config.employee_id));
          dispatch(setEmployeeConfigId(payload.user.config.employee_config_id));
          dispatch(setModuleFilter(payload));
          dispatch(setModuleHiddenColumns(payload));
          dispatch(setModuleColumnsWidth(payload));
          dispatch(setModuleColumnsSequence(payload));
          dispatch(setModuleSearchFields(payload));
        }
      }
    } else {
      setSeverity('error');
      setTitle('Podano email i/lub hasło są nieprawidłowe');
      setMessage('Spróbuj ponownie jeszcze raz');
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      component={Paper}
      elevation={4}
    >
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <FormControl
          style={{
            marginTop: 20,
            marginBottom: 20,
            marginLeft: 20,
            marginRight: 20,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography color="primary" variant="h6">
            Adres e-mail
          </Typography>
          <DebounceInput
            variant="outlined"
            name="email"
            id="email"
            type="text"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            debounceTimeout={100}
            element={TextField}
            style={{ marginBottom: 10 }}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <FormControl
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: 20,
            marginLeft: 20,
            marginRight: 20
          }}
        >
          <Typography color="primary" variant="h6">
            Hasło
          </Typography>
          <DebounceInput
            variant="outlined"
            type="password"
            name="password"
            id="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            debounceTimeout={100}
            element={TextField}
            style={{ marginBottom: 10 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOpenIcon />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <Divider style={{ width: mediaWidth, marginTop: 20, marginBottom: 20 }} />
        <Button
          color="primary"
          variant="contained"
          type="submit"
          style={{ marginBottom: 20, marginLeft: 20, marginRight: 20 }}
        >
          Zaloguj
        </Button>
      </form>
      <Button onClick={() => navigate('/restore-password')}>
        <Typography variant="subtitle2">Przypomnij hasło</Typography>
      </Button>
    </Box>
  );
};
